import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { formatDate } from '../../lib/format';
import { platformLabel } from '../../lib/platforms';
import { domainOf, safeExternalUrl } from '../../lib/url';
import type { CalendarEventRead, CampaignRead, PlannedPostRead } from '../../types/api';
import { automationLabel, errorLabel, isResettable, statusLabel } from './automation';
import { dayLabel, timeLabel } from './calendarDates';

/** Everything filed under one date, in the order the list renders it. */
export interface DayEntries {
  iso: string;
  events: CalendarEventRead[];
  posts: PlannedPostRead[];
}

interface UpcomingListProps {
  days: readonly DayEntries[];
  campaignsById: ReadonlyMap<string, CampaignRead>;
  /** Day the grid sent us to, from a "+N more". Highlighted and scrolled to. */
  anchor: string | null;
  /** What an empty list says: nothing ahead, or nothing in the month shown. */
  emptyText: string;
  onOpenPost: (post: PlannedPostRead) => void;
  onOpenEvent: (event: CalendarEventRead) => void;
  onPlanPost: () => void;
  /** A parked or failed plan back to the scheduler. */
  onResetPost: (post: PlannedPostRead) => void;
}

/**
 * The second line of a planned post: platform, campaign, and — for a plan that
 * runs itself — its mode, its time and where it got to.
 */
function postMeta(post: PlannedPostRead, campaign: CampaignRead | null): string {
  const parts = [post.platform ? platformLabel(post.platform) : 'Platform not set'];
  if (campaign) parts.push(campaign.name);
  if (post.automation !== 'none') {
    parts.push(automationLabel(post.automation));
    if (post.publish_at) parts.push(timeLabel(post.publish_at));
    const status = statusLabel(post.automation_status);
    if (status) parts.push(status);
  }
  return parts.join(' · ');
}

interface DayEntryListProps {
  day: DayEntries;
  campaignsById: ReadonlyMap<string, CampaignRead>;
  onOpenPost: (post: PlannedPostRead) => void;
  onOpenEvent: (event: CalendarEventRead) => void;
  /** A parked or failed plan back to the scheduler. */
  onResetPost: (post: PlannedPostRead) => void;
}

/**
 * One day's entries: its key dates, then its planned posts, each a full-width
 * row with its meta line, the reason a plan stopped, and its way on. The list
 * view draws one per day; the phone's day agenda draws the one day picked.
 */
export function DayEntryList({
  day,
  campaignsById,
  onOpenPost,
  onOpenEvent,
  onResetPost,
}: DayEntryListProps) {
  return (
    <ul className="upcoming-day__entries">
      {day.events.map((event) => (
        <li key={`event-${event.id}`}>
          <button
            className="upcoming-entry upcoming-entry--event"
            type="button"
            onClick={() => onOpenEvent(event)}
          >
            <span className="upcoming-entry__title">{event.title}</span>
            <span className="upcoming-entry__meta">
              Event
              {event.ends_on && event.ends_on !== event.starts_on
                ? ` · to ${formatDate(event.ends_on)}`
                : ''}
            </span>
          </button>
        </li>
      ))}

      {day.posts.map((post) => {
        const campaign =
          post.campaign_id === null ? null : (campaignsById.get(post.campaign_id) ?? null);
        // A post planned off a news article keeps the way back to it. The
        // link is a sibling of the entry, never nested inside that button.
        const sourceHref = safeExternalUrl(post.source_url);
        const reason = errorLabel(post.automation_error);
        const reviewable =
          post.generation_request_id !== null &&
          (post.automation_status === 'needs_review' ||
            post.automation_status === 'generating' ||
            post.automation_status === 'publishing' ||
            post.automation_status === 'published');

        const classes = ['upcoming-entry'];
        if (post.automation_status === 'needs_review') classes.push('upcoming-entry--review');
        if (post.automation_status === 'failed') classes.push('upcoming-entry--failed');

        return (
          <li className="upcoming-entry-row" key={`post-${post.id}`}>
            <button
              className={classes.join(' ')}
              type="button"
              style={campaign ? { borderLeftColor: campaign.color } : undefined}
              onClick={() => onOpenPost(post)}
            >
              <span className="upcoming-entry__title">{post.title}</span>
              <span className="upcoming-entry__meta">{postMeta(post, campaign)}</span>
            </button>
            {reason ? <p className="upcoming-entry__reason">{reason}</p> : null}
            {sourceHref || reviewable || isResettable(post) ? (
              <div className="upcoming-entry__links">
                {sourceHref ? (
                  <a
                    className="source-chip"
                    href={sourceHref}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {domainOf(sourceHref) ?? sourceHref}
                  </a>
                ) : null}
                {reviewable && post.generation_request_id ? (
                  <Link className="source-chip" to={`/review/${post.generation_request_id}`}>
                    {post.automation_status === 'needs_review' ? 'Review' : 'Open'}
                  </Link>
                ) : null}
                {isResettable(post) ? (
                  <button
                    className="source-chip source-chip--button"
                    type="button"
                    onClick={() => onResetPost(post)}
                  >
                    Reset
                  </button>
                ) : null}
              </div>
            ) : null}
          </li>
        );
      })}
    </ul>
  );
}

/**
 * What is coming up, from today forward, in one column; or, for any month other
 * than this one, that month's days alone.
 *
 * The grid's overflow lands here: a day with more posts than a cell can hold
 * gets shown in full rather than truncated harder. It is also where an
 * automated plan says what happened to it in words, and offers the way on:
 * the review page while the worker has a request for it, a reset when it
 * parked or failed.
 */
export function UpcomingList({
  days,
  campaignsById,
  anchor,
  emptyText,
  onOpenPost,
  onOpenEvent,
  onPlanPost,
  onResetPost,
}: UpcomingListProps) {
  const listRef = useRef<HTMLOListElement>(null);

  useEffect(() => {
    if (anchor === null) return;
    const target = listRef.current?.querySelector('[data-anchor="true"]');
    target?.scrollIntoView({ block: 'nearest' });
  }, [anchor]);

  if (days.length === 0) {
    return (
      <div className="kb-section__empty">
        <p className="kb-section__empty-text">{emptyText}</p>
        <button className="button" type="button" onClick={onPlanPost}>
          Plan a post
        </button>
      </div>
    );
  }

  return (
    <ol className="upcoming-list" ref={listRef}>
      {days.map((day) => (
        <li
          className={day.iso === anchor ? 'upcoming-day upcoming-day--anchor' : 'upcoming-day'}
          key={day.iso}
          data-anchor={day.iso === anchor ? 'true' : undefined}
        >
          <h3 className="upcoming-day__date">{dayLabel(day.iso)}</h3>

          <DayEntryList
            day={day}
            campaignsById={campaignsById}
            onOpenPost={onOpenPost}
            onOpenEvent={onOpenEvent}
            onResetPost={onResetPost}
          />
        </li>
      ))}
    </ol>
  );
}
